import { Badge, Button, IconButton, Text, Tooltip } from '@elkaso-app/web-design';
import { useGetDocumentTypesApi } from 'apis/document-vault/use-get-document-types-api';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, ClearFilterButton, TableHeader } from 'components/base-table/components';
import { useCallback, useMemo, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { DocumentTypeModal } from '../components/document-type-modal';
import { DeleteDocumentTypeModal } from '../components/delete-document-type-modal';

const categoryBadgeStyles: Record<string, { color: string; bgColor: string }> = {
  BUSINESS_REGISTRATION: { color: '#3182CE', bgColor: 'rgba(49, 130, 206, 0.08)' },
  OWNER_IDENTIFICATION: { color: '#805AD5', bgColor: 'rgba(128, 90, 213, 0.08)' },
  FINANCIAL_DOCUMENTS: { color: '#DD6B20', bgColor: 'rgba(221, 107, 32, 0.08)' },
};

const categoryLabels: Record<string, string> = {
  BUSINESS_REGISTRATION: 'Business Registration',
  OWNER_IDENTIFICATION: 'Owner Identification',
  FINANCIAL_DOCUMENTS: 'Financial Documents',
};

export const DocumentTypesListScreen = () => {
  const { data, isLoading, isFetching } = useGetDocumentTypesApi();
  const documentTypes = data?.data ?? data ?? [];

  const [selectedType, setSelectedType] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = useCallback((docType: any) => {
    setSelectedType(docType);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((docType: any) => {
    setSelectedType(docType);
    setIsDeleteModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Name' />,
        accessor: 'name',
        Cell: ({ value }: CellProps<any, string>) => (
          <Text fontWeight='semibold' fontSize='sm' color='gray.800'>
            {value}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Category' />,
        accessor: 'category',
        Cell: ({ value }: CellProps<any, string>) => (
          <Badge
            color={categoryBadgeStyles[value]?.color}
            bgColor={categoryBadgeStyles[value]?.bgColor}
            px='md'
            py='sm'
            borderRadius='full'
            fontSize='xs'>
            {categoryLabels[value] ?? value}
          </Badge>
        ),
      },
      {
        Header: <TableHeader name='Required' />,
        accessor: 'isRequired',
        Cell: ({ value }: CellProps<any, boolean>) => (
          <Text fontSize='sm' color={value ? 'green.500' : 'gray.400'}>
            {value ? 'Yes' : 'No'}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Has Expiry' />,
        accessor: 'hasExpiry',
        Cell: ({ value }: CellProps<any, boolean>) => (
          <Text fontSize='sm' color={value ? 'orange.500' : 'gray.400'}>
            {value ? 'Yes' : 'No'}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Sort Order' />,
        accessor: 'sortOrder',
        Cell: ({ value }: CellProps<any, number>) => (
          <Text fontSize='sm' color='gray.600'>
            {value}
          </Text>
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'id',
        Cell: ({ row }: CellProps<any, any>) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            <Tooltip label='Edit' placement='top'>
              <IconButton
                aria-label='Edit document type'
                icon={<FiEdit2 />}
                size='sm'
                variant='ghost'
                colorScheme='blue'
                onClick={() => handleEdit(row.original)}
              />
            </Tooltip>
            <Tooltip label='Delete' placement='top'>
              <IconButton
                aria-label='Delete document type'
                icon={<FiTrash2 />}
                size='sm'
                variant='ghost'
                colorScheme='red'
                onClick={() => handleDelete(row.original)}
              />
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  const tableData = useMemo(() => (Array.isArray(documentTypes) ? documentTypes : []), [documentTypes]);

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Document Types' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <Button leftIcon={<FiPlus />} colorScheme='red' size='sm' onClick={() => setIsCreateModalOpen(true)}>
            Add Document Type
          </Button>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right />
      </BaseTableContainer.Footer>

      <DocumentTypeModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} mode='create' />

      {selectedType && (
        <>
          <DocumentTypeModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedType(null);
            }}
            mode='edit'
            documentType={selectedType}
          />
          <DeleteDocumentTypeModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedType(null);
            }}
            documentType={selectedType}
          />
        </>
      )}
    </BaseTableContainer>
  );
};
